import { apiFetch } from "./client";
import type { Page } from "./admin";

/**
 * Proizvodni nalozi, ono što Bankom zove „narudžbenica kupca".
 *
 * Ime obmanjuje, pa stoji i ovde: dokument po imenu deluje kao kupčev, a piše ga Bankomov
 * komercijalista i šalje ga u sopstvenu proizvodnju. U korisničkom delu piše „narudžbenica", jer
 * tako govore; u kodu je proizvodni nalog.
 *
 * Cene nema nigde, ni na zaglavlju ni na stavci, i to nije propust: nalog ide proizvodnji kojoj
 * cena ne treba.
 */

export type ProductionOrderStatus = "NOVA" | "U_PROIZVODNJI" | "ZAVRSENA" | "OTKAZANA";

export type ProductionOrderLine = {
  id: string;
  position: number;
  productId: string | null;
  productName: string | null;
  sku: string | null;
  freeTextDescription: string | null;
  quantity: number;
  unit: string;
  /** Stavka porudžbine koju ovaj red pokriva. */
  sourceOrderLineId: string | null;
  note: string | null;
};

export type ProductionOrder = {
  id: string;
  orderNumber: string;
  status: ProductionOrderStatus;
  companyId: string;
  companyName: string;
  sourceOrderId: string | null;
  sourceOrderNumber: string | null;
  neededBy: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  lines: ProductionOrderLine[];
  /** Made by itself with the ERP pro forma invoice at the approval, not by a click. */
  autoCreated: boolean;
  /** Who made it: by a click the one who clicked, made by itself the approver; null when brought from the ERP. */
  createdByName: string | null;
  fromErp: boolean;
};

export type ProductionOrderRow = {
  id: string;
  orderNumber: string;
  status: ProductionOrderStatus;
  companyId: string;
  companyName: string;
  companyCountry: string | null;
  sourceOrderId: string | null;
  sourceOrderNumber: string | null;
  neededBy: string | null;
  lineCount: number;
  totalQuantity: number;
  createdAt: string;
  autoCreated: boolean;
  createdByName: string | null;
  fromErp: boolean;
};

export type ProductionOrderLineInput = {
  productId?: string;
  freeTextDescription?: string;
  quantity: number;
  unit?: string;
  sourceOrderLineId?: string;
  note?: string;
};

const q = (params: Record<string, string | number | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    usp.set(k, String(v));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const productionOrdersApi = {
  list: (
    p: {
      companyId?: string;
      status?: string;
      dateFrom?: string;
      dateTo?: string;
      q?: string;
      page?: number;
      size?: number;
    } = {},
  ) => apiFetch<Page<ProductionOrderRow>>(`/production-orders${q(p)}`),

  get: (id: string) => apiFetch<ProductionOrder>(`/production-orders/${id}`),

  /** Nalozi jedne porudžbine: odgovor kupcu dokle mu je roba. */
  byOrder: (orderId: string) =>
    apiFetch<ProductionOrderRow[]>(`/production-orders/by-order/${orderId}`),

  create: (body: {
    companyId?: string;
    sourceOrderId?: string;
    neededBy?: string;
    note?: string;
    lines: ProductionOrderLineInput[];
  }) =>
    apiFetch<ProductionOrder>("/production-orders", {
      method: "POST",
      body,
    }),

  setStatus: (id: string, status: ProductionOrderStatus) =>
    apiFetch<ProductionOrder>(`/production-orders/${id}/status`, {
      method: "PATCH",
      body: { status },
    }),

  remove: (id: string) =>
    apiFetch<void>(`/production-orders/${id}`, { method: "DELETE" }),
};
